// Pure data + helpers for the phone bottom tab bar (Mobile Parity
// Program, Wave 0 slice 1 — docs/design/mobile-parity-program.md).
//
// No JSX here (same reasoning as top-nav-helpers.ts): the node-pool
// test suite imports this directly, and MobileTabBar.tsx stays a thin
// renderer.
//
// THE SHAPE: five slots on a phone — Home · Households · Ask Stew
// (center) · a role-aware "work" slot · More. The bar is the thumb-reach
// shortlist ("default to your desk"); the More slot opens the existing
// full nav drawer, so EVERY destination stays reachable — the bar
// narrows the starting point, never the reach (full-parity bar,
// Grant 2026-08-28).

import type { RoleAwareUser } from "../utils/role-visibility";

/** Icon vocabulary for tab items — mapped to Lucide components in
 *  MobileTabBar.tsx (kept as strings so this module stays JSX-free). */
export type MobileTabIcon =
  | "home"
  | "households"
  | "renewals"
  | "processing"
  | "opportunities"
  | "tasks"
  | "today"
  | "referrals";

export interface MobileTab {
  /** Stable key for active-state + React keys. */
  key: string;
  label: string;
  to: string;
  icon: MobileTabIcon;
}

const HOME_TAB: MobileTab = {
  key: "home",
  label: "Home",
  to: "/dashboard",
  icon: "home",
};

const HOUSEHOLDS_TAB: MobileTab = {
  key: "households",
  label: "Households",
  to: "/households",
  icon: "households",
};

// The role-aware third link. DECISION (2026-08-28, flagged for Grant to
// correct per role): the slot holds each role's daily work queue —
// proposed from the role dashboards, not yet confirmed person-by-person.
//   * Insurance-scoped users (Joy, Brandon, Mylah) live in renewals.
//   * Mortgage + Realty are Processing companies (loans/transactions in
//     flight are the daily anchor).
//   * Financial + Planning lean sales/review, so Opportunities.
//   * Front desk (Donna) gets Today (My Day) — her work is the day's
//     schedule, not any one company's pipeline.
//   * Admin/compliance/unscoped get Tasks — the only queue that spans
//     everything.
// Mixed company scopes resolve by the priority order below (first
// match wins) — e.g. Jake (financial + planning) → Opportunities.
const RENEWALS_TAB: MobileTab = {
  key: "renewals",
  label: "Renewals",
  to: "/ongoing-care?f=type:renewal_plan",
  icon: "renewals",
};
const PROCESSING_TAB: MobileTab = {
  key: "processing",
  label: "Processing",
  to: "/processing",
  icon: "processing",
};
const OPPORTUNITIES_TAB: MobileTab = {
  key: "opportunities",
  label: "Opportunities",
  to: "/opportunities",
  icon: "opportunities",
};
const TASKS_TAB: MobileTab = {
  key: "tasks",
  label: "Tasks",
  to: "/tasks",
  icon: "tasks",
};
const TODAY_TAB: MobileTab = {
  key: "today",
  label: "Today",
  to: "/my-day",
  icon: "today",
};

const COMPANY_WORK_TABS: ReadonlyArray<[slug: string, tab: MobileTab]> = [
  ["insurance", RENEWALS_TAB],
  ["mortgage", PROCESSING_TAB],
  ["realty", PROCESSING_TAB],
  ["financial", OPPORTUNITIES_TAB],
  ["planning", OPPORTUNITIES_TAB],
];

function pickWorkTab(user: RoleAwareUser): MobileTab {
  const role = user?.employee_role_slug ?? null;
  if (role === "front_desk") return TODAY_TAB;
  // Mylah (back_office) is Insurance data work — renewals over tasks.
  if (role === "back_office") return RENEWALS_TAB;
  if (role === "system_admin" || role === "compliance") return TASKS_TAB;
  const scope = user?.editable_company_slugs;
  if (Array.isArray(scope)) {
    for (const [slug, tab] of COMPANY_WORK_TABS) {
      if (scope.includes(slug)) return tab;
    }
  }
  // Unscoped ("all") or unknown — Tasks is the queue every role has.
  return TASKS_TAB;
}

/**
 * The LINK tabs for this user, in display order. The component
 * interleaves the two fixed action slots (Ask Stew center button, More
 * drawer trigger) around these — they're UI actions, not routes, so
 * they don't live in this data.
 *
 * Ambassador Agents (deny-by-default role) get only their one real
 * destination: every standard lane 403s for them server-side, and Ask
 * Stew's firm-wide search is not theirs either — so the component
 * renders just Referrals + More for them (mirrors visibleNavItems).
 */
export function pickMobileTabs(user: RoleAwareUser): MobileTab[] {
  if (user?.employee_role_slug === "ambassador") {
    return [
      { key: "referrals", label: "Referrals", to: "/referrals", icon: "referrals" },
    ];
  }
  return [HOME_TAB, HOUSEHOLDS_TAB, pickWorkTab(user)];
}

/**
 * Which tab (by key) the current location belongs to, or null. Same
 * exact-or-descendant prefix matching as activeNavItemFor, but on the
 * tab's PATH only — a tab whose `to` carries a query filter (Renewals →
 * /ongoing-care?f=…) still owns every /ongoing-care location, because
 * on a phone the bar is answering "what area am I in", not "which saved
 * filter is applied".
 */
export function activeMobileTabKey(
  pathname: string,
  tabs: readonly MobileTab[],
): string | null {
  const withPaths = tabs.map((tab) => ({
    tab,
    path: tab.to.split("?")[0] ?? tab.to,
  }));
  // Longest path first so /dashboard/insurance can't match a shorter
  // sibling before its own tab.
  withPaths.sort((a, b) => b.path.length - a.path.length);
  for (const { tab, path } of withPaths) {
    if (pathname === path || pathname.startsWith(path + "/")) return tab.key;
  }
  return null;
}
